import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import debounce from 'lodash/debounce';
import Pusher from 'pusher-js';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import Button from 'components/Button';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import SearchField from 'components/SearchField';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { fetchQBDTimeeroPaginatedEmployee } from 'hooks/integrations/useFetchPaginatedQBTimeeroEmployees';
import {
  useFetchPaginatedQuickbookDesktopEmployees,
  IQuickbookDesktopEmployee,
} from 'hooks/integrations/useFetchPaginatedQuickbookDesktopEmployee';
import { useFetchQBDQueueEmployeeCustomerList } from 'hooks/integrations/useFetchQBDQueueEmployeeCustomerList';
import {
  IBulkQuickbookDesktopEmployee,
  useMapBulkQuickbookDesktopEmployee,
} from 'hooks/integrations/useMapBulkQBDEmployee';
import {
  useMapQuickbookDesktopEmployee,
  IMapQuickbookDesktopEmployee,
} from 'hooks/integrations/useMapQuickbookDesktopEmployee';
import {
  IQuickbookDesktopUnmapEmployee,
  useUnmapQuickbookDesktopEmployee,
} from 'hooks/integrations/useUnmapQuickbookDesktopEmployee';
import { getAsyncSelectLoadOptions } from 'utils/helpers';
import toaster from 'utils/toaster';

const initialSearchQuery: IQuickbookDesktopEmployee = {
  page: 1,
  page_size: 25,
  search: '',
};

const EmployeeMapping = () => {
  const [mappedUsers, setMappedUsers] = useState<any>({});
  const [currentMapUser, setCurrentMapUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IQuickbookDesktopEmployee>(initialSearchQuery);

  const { t } = useTranslation(['integrations', 'common', 'employees']);

  const {
    data: qbdEmployeeData,
    status: qbdEmployeeStatus,
    refetch,
  } = useFetchPaginatedQuickbookDesktopEmployees(searchQuery);
  const { status: qbdMapEmployeeStatus, mutateAsync: qbdMapEmployeeMutateAsync } = useMapQuickbookDesktopEmployee();
  const { status: qbUnmapEmployeeStatus, mutateAsync: qbUnmapEmployeeMutateAsync } = useUnmapQuickbookDesktopEmployee();
  const { status: qbdUnmapEmployeeStatus, mutateAsync: qbdUnmapEmployeeMutateAsync } =
    useMapBulkQuickbookDesktopEmployee();
  useFetchQBDQueueEmployeeCustomerList();

  // checks for new data every 5 seconds

  useEffect(
    () => {
      const apiFetch = setInterval(() => {
        refetch();
      }, 60000);

      return () => {
        clearInterval(apiFetch);
      };
    },
    // eslint-disable-next-line
    []
  );

  useEffect(
    () => {
      const pusher = new Pusher(import.meta.env.REACT_PUSHER_API_KEY || '', {
        cluster: import.meta.env.REACT_PUSHER_CLUSTER || '',
      });
      const channel = pusher.subscribe(import.meta.env.REACT_PUSHER_CHANNEL || '');
      channel.bind(import.meta.env.REACT_PUSHER_EVENT || '', () => {
        refetch();
      });

      return () => {
        pusher.unsubscribe(import.meta.env.REACT_PUSHER_CHANNEL || '');
      };
    }, // eslint-disable-next-line
    []
  );

  useEffect(
    () => {
      if (qbdMapEmployeeStatus === 'success') {
        qbdEmployeeData?.payload.forEach((employee: any) => {
          if (!mappedUsers.hasOwnProperty(employee.id) && employee.selected_user) {
            setMappedUsers((prevValue: any) => ({
              ...prevValue,
              [employee.id]: {
                employee: employee.selected_user,
              },
            }));
          }
        });
      }
    },
    // eslint-disable-next-line
    [qbdMapEmployeeStatus]
  );

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('common:name'),
      accessor: 'employee_name',
      Cell: (data: CellValue) => {
        return (
          <Text textOverflow="ellipsis" overflow="hidden">
            {data?.row?.original?.quickbook_employee_name}
          </Text>
        );
      },
    },
    {
      Header: t('common:email'),
      accessor: 'email',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <Text textOverflow="ellipsis" overflow="hidden">
            {data?.row?.original?.selected_user?.email}
          </Text>
        );
      },
    },
    {
      Header: t('common:status'),
      accessor: 'status',
      width: 80,
    },
    {
      Header: t('integrations:timeeroUsers'),
      accessor: 'role',
      width: 200,
      Cell: (data: CellValue) => {
        return (
          <Box position="relative" width="100%">
            {qbdMapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id && (
              <Flex
                width="100%"
                height="100%"
                position="absolute"
                zIndex={2}
                justifyContent="center"
                alignItems="center"
                background="rgba(255, 255, 255, 0.7)"
              >
                <Spinner size="xs" />
              </Flex>
            )}
            <ReactSelectAsyncPaginate
              loadOptions={employeeLoadOptions}
              additional={{
                page: 1,
              }}
              value={
                data.row.original?.selected_user
                  ? data.row.original?.selected_user || null
                  : mappedUsers &&
                      mappedUsers[`${data.row.original.id}`] &&
                      mappedUsers[`${data.row.original.id}`].employee
                    ? mappedUsers[`${data.row.original.id}`].employee
                    : null
              }
              onChange={(value: any) => {
                setMappedUsers((prevValue: any) => ({
                  ...prevValue,
                  [data.row.original.id]: {
                    employee: value || null,
                  },
                }));
              }}
              size="md"
              variant="filled"
              fontSize="sm"
              width="100%"
              debounceTimeout={300}
              menuPortalTarget={document.body}
              isSearchable
              placeholder={t('common:selectUser')}
              getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}`}
              getOptionValue={(option: any) => option.id}
            />
          </Box>
        );
      },
    },
    {
      Header: '',
      accessor: 'first',
      Cell: (data: CellValue) => {
        return data.row.original?.selected_user ? (
          <Button
            _hover={{ bg: 'secondary.100' }}
            fontWeight="bold"
            background="rgba(250, 85, 85, 0.2)"
            color="rgba(250, 85, 85, 1)"
            isLoading={qbUnmapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onUnmapEmployee(data.row.original.id, data.row.original.selected_user.id)}
          >
            {t('integrations:removeMapping')}
          </Button>
        ) : (
          <Button
            _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
            background="rgba(255, 103, 32, 0.1)"
            colorScheme="rgba(255, 103, 32, 0.3)"
            color="secondary.500"
            isLoading={qbdMapEmployeeStatus === 'loading' && currentMapUser === data.row.original.id}
            onClick={() => onMapEmployee(data.row.original.id)}
          >
            {t('integrations:mapUser')}
          </Button>
        );
      },
    },
  ];

  useEffect(
    () => {
      if (qbdEmployeeStatus === 'success') {
        qbdEmployeeData?.payload.forEach((employee: any) => {
          if (!mappedUsers.hasOwnProperty(employee.id) && employee.selected_user) {
            setMappedUsers((prevValue: any) => ({
              ...prevValue,
              [employee.id]: {
                employee: employee.selected_user,
              },
            }));
          }
        });
      }
    },
    // eslint-disable-next-line
    [qbdEmployeeStatus]
  );

  const onMapEmployee = (quickbookId: number) => {
    if (mappedUsers && mappedUsers[quickbookId] && mappedUsers[quickbookId].hasOwnProperty('employee')) {
      setCurrentMapUser(quickbookId);
      const postData: IMapQuickbookDesktopEmployee = {
        user_id: mappedUsers[quickbookId].employee.id,
        quick_book_employee_id: quickbookId,
      };
      qbdMapEmployeeMutateAsync(postData).then(() => {
        refetch();
      });
    } else {
      toaster.error({ description: t('integrations:employeeSelectMapError') });
    }
  };

  const onUnmapEmployee = (quickbookId: number, userId: string) => {
    setCurrentMapUser(quickbookId);

    const postData: IQuickbookDesktopUnmapEmployee = {
      user_id: userId,
      quick_book_employee_id: quickbookId,
    };
    qbUnmapEmployeeMutateAsync(postData).then(() => {
      setMappedUsers(null);
      refetch();
    });
  };

  const onBulkEmployeeMap = (selected: any) => {
    let isAllMapped = true;
    const mappedUsersArray = [];
    for (let i = 0; i < selected.length; i++) {
      if (!mappedUsers.hasOwnProperty(selected[i])) {
        isAllMapped = false;
      } else {
        mappedUsersArray.push(mappedUsers[selected[i]]?.employee.id || mappedUsers[selected[i]].employee.id);
      }
    }
    if (!isAllMapped) {
      toaster.error({ description: t('integrations:employeeAllSelectedMapError') });
      return;
    }

    const postData: IBulkQuickbookDesktopEmployee = {
      quick_book_employee_ids: selected,
      user_ids: mappedUsersArray,
    };
    qbdUnmapEmployeeMutateAsync(postData).then(() => {
      refetch();
    });
  };

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchQBDTimeeroPaginatedEmployee, { page, search });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();
      setSearchQuery({ ...searchQuery, page: 1, search: searchValue });
    }, 400),
    [searchQuery]
  );

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IQuickbookDesktopEmployee) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  return (
    <Box>
      <Title>{t('integrations:quickbooksDesktop.desktopMapping')}</Title>
      <Flex justifyContent="space-between">
        <Text variant="h2" fontSize="lg" ml={10} mt={4}>
          {t('integrations:mapFieldsPlatforms')}
        </Text>
      </Flex>
      <Box ml="auto" mt={4} maxWidth={{ md: '250px' }} width="100%">
        <SearchField
          inputProps={{
            placeholder: t('common:searchUsers'),
            onChange: handleSearchInputChange,
          }}
        />
      </Box>

      <Table
        columns={columns}
        data={qbdEmployeeData?.payload || []}
        isLoading={qbdEmployeeStatus === 'loading'}
        enableRowSelect
        paginationData={qbdEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        renderTableFooter={(selectedFlatRows: any) => {
          const selected = selectedFlatRows.map((item: any) => item.original.id);
          return (
            <Flex
              width="100%"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              border="1px"
              borderColor="primary.100"
              boxShadow="0 -4px 4px -1px rgba(0, 0, 0, 0.1),0 -2px 2px -1px rgba(0, 0, 0, 0.06);"
              px="6"
              py="4"
            >
              <Text fontWeight="semibold">
                {selectedFlatRows.length} {t('common:selected')}
              </Text>
              <Button
                _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
                background="rgba(255, 103, 32, 0.1)"
                isLoading={qbdUnmapEmployeeStatus === 'loading'}
                onClick={() => onBulkEmployeeMap(selected)}
                color="secondary.500"
              >
                {t('integrations:mapEmployees')}
              </Button>
            </Flex>
          );
        }}
      />
    </Box>
  );
};
export default EmployeeMapping;
